import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  Grid,
  Paper,
  TableCell,
  TableRow,
  useTheme,
} from "@mui/material";
import { Loading, LoadingFailed } from "~/components/loading";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { ComplianceTable } from "~/components/Completion/ComplianceTable";
import {
  ControlsOrderField,
  ControlState,
  GetComplianceControlDocument,
  GetComplianceFrameworkControlsDocument,
  GetComplianceFrameworkControlsQuery,
  GetComplianceFrameworkDocument,
  OrderDirection,
  ExceptionStatus,
  TestIamActionsQuery,
  useGetComplianceFrameworkControlsQuery,
} from "~/operations";
import { useSearch } from "~/components/search/useSearch";
import { CompletionCell } from "../../components/CompletionCell";
import { useCanApplyExceptionMutation } from "~/lib/iam/actions/mondoo/policy/ExtendedHub/ApplyExceptionMutation";
import {
  INITIAL_PAGE_RANGE,
  Pagination,
  PaginationRange,
} from "~/components/pagination";
import { useExceptions } from "~/components/exceptions/use-exceptions";
import { OutOfScopeChip } from "~/components/exceptions/out-of-scope-chip";
import { mapSelectedEntitiesToString } from "~/components/exceptions/utils/mapSelectedEntitiesToString";
import { useFrameworkContext } from "~/pages/compliance/Framework/Framework";
import { ExceptionChip } from "~/components";
import { useDialogs } from "@toolpad/core";
import { RemoveExceptionsGroupDialog } from "~/components/exceptions/RemoveExceptionsGroupDialog";
import {
  SetExceptionDialog,
  SetExceptionDialogPayload,
} from "~/components/exceptions/SetExceptionDialog";
import { setDocumentTitle } from "~/lib/commonUtils";
import { useTriageToolbar } from "~/providers/triage-toolbar";
import { TriageToolbarButtonType } from "~/components/TriageToolbar/types";
import { ExceptionTooltipContent } from "~/components/exceptions/ExceptionTooltipContent/ExceptionTooltipContent";
import { useExceptionsButtons } from "~/components/exceptions/use-exceptions-buttons";
import { useSelection } from "~/hooks/useSelection";
import { SelectedEntityWithException } from "~/components/exceptions/types";
import { Header } from "~/types/table";

type ControlsConnection = NonNullable<
  GetComplianceFrameworkControlsQuery["complianceFramework"]["controls"]
>;
type ControlEdge = NonNullable<ControlsConnection["edges"]>[0];
type ControlNode = NonNullable<ControlEdge["node"]>;

export type ControlsProps = {};

export function FrameworkControls({}: ControlsProps) {
  const { space, framework, exceptionGroups } = useFrameworkContext();
  const [searchParams] = useSearchParams();
  const search = useSearch();
  const [pageItems, setPageItems] =
    useState<PaginationRange>(INITIAL_PAGE_RANGE);
  const navigate = useNavigate();
  const frameworkMrn = searchParams.get("frameworkMrn") || "";
  const frameworkId = frameworkMrn.split("/").pop();
  const theme = useTheme();
  const dialogs = useDialogs();
  const triageToolbar = useTriageToolbar();

  const hasApplyExceptionPermission = useCanApplyExceptionMutation();

  const {
    isGroupChecked,
    isGroupIndeterminate,
    onGroupCheckChange,
    selectedEntities: selectedControls,
    setSelectedEntities: setSelectedControls,
    handleNodeClick,
    handleNodeChange,
    handleCancelClick,
  } = useSelection<SelectedEntityWithException & { title: string }>();

  const {
    exceptionsButtonsAvailability,
    exceptionsButtonsVisibility,
    exceptionsButtonsTooltipText,
  } = useExceptionsButtons({
    selectedEntities: selectedControls,
    target: "control",
    showExceptionButtons: true,
    showScopeButtons: true,
  });

  useEffect(() => {
    if (selectedControls.length > 0) {
      triageToolbar.show({
        buttons: [
          ...(exceptionsButtonsVisibility.SET_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.CreateException,
                  onClick: handleSetExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.SET_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.SET_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.REMOVE_EXCEPTION
            ? [
                {
                  type: TriageToolbarButtonType.RemoveException,
                  onClick: handleRemoveExceptionModalOpen,
                  disabled: !exceptionsButtonsAvailability.REMOVE_EXCEPTION,
                  tooltip: exceptionsButtonsTooltipText.REMOVE_EXCEPTION,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.SET_IN_SCOPE
            ? [
                {
                  type: TriageToolbarButtonType.SetInScope,
                  onClick: async () => {
                    await handleRemoveExceptionGroups({
                      exceptionMrns: selectedControls
                        .map((c) => c.exception?.exceptionMrn)
                        .filter((mrn): mrn is string => Boolean(mrn)),
                    });
                    setSelectedControls([]);
                  },
                  disabled: !exceptionsButtonsAvailability.SET_IN_SCOPE,
                  tooltip: exceptionsButtonsTooltipText.SET_IN_SCOPE,
                },
              ]
            : []),
          ...(exceptionsButtonsVisibility.SET_OUT_OF_SCOPE
            ? [
                {
                  type: TriageToolbarButtonType.SetOutOfScope,
                  onClick: () => handleSetScope(false),
                  disabled: !exceptionsButtonsAvailability.SET_OUT_OF_SCOPE,
                  tooltip: exceptionsButtonsTooltipText.SET_OUT_OF_SCOPE,
                },
              ]
            : []),
        ],
        payload: {
          count: selectedControls.length,
          totalCount: controlsArray.length,
          onClose: handleCancelClick,
        },
      });
    } else {
      triageToolbar.hide();
    }

    return () => {
      triageToolbar.hide();
    };
  }, [selectedControls]);

  const {
    data: { complianceFramework } = {},
    error,
    loading,
  } = useGetComplianceFrameworkControlsQuery({
    variables: {
      input: {
        frameworkMrn: framework.mrn,
        scopeMrn: space.mrn,
      },
      controlsInput: {
        after: null,
        limit: 50,
        orderBy: {
          field:
            (searchParams.get("field") as ControlsOrderField) ||
            ControlsOrderField.Assets,
          direction:
            (searchParams.get("direction") as OrderDirection) ||
            OrderDirection.Desc,
        },
        query: search.searchFilters.join(","),
      },
    },
  });

  const { handleRemoveExceptionGroups, handleSetException, handleSetScope } =
    useExceptions({
      onSetException: () => {
        setSelectedControls([]);
      },
      scopeMrns: [space.mrn],
      controlMrns: mapSelectedEntitiesToString(selectedControls),
      refetchQueries: [
        GetComplianceFrameworkDocument,
        GetComplianceFrameworkControlsDocument,
        GetComplianceControlDocument,
      ],
    });

  const { controls } = complianceFramework || {};

  setDocumentTitle(["Controls", framework.name, "Compliance", space.name]);

  if (loading) {
    return <Loading what="controls" />;
  }

  if (error || !controls) {
    return <LoadingFailed what="controls" />;
  }

  const controlEdges = controls.edges ?? [];
  const controlsArray = controlEdges.flatMap((edge) => edge?.node ?? []);

  const targetPaginatedGroup = controlsArray
    .slice(pageItems.from, pageItems.to)
    .map((control) => ({
      mrn: control.mrn as string,
      exception: control.exception,
      title: control.title,
    }));

  // if we're on the controls tab, we only need to include
  // the control name in the URL, otherwise we need to include "controls"
  const buildControlLink = (
    control: ControlNode,
    subpath = "",
    params: string[][] = [],
  ) => {
    const controlId = control.mrn?.split("/").pop();
    const base = `/space/compliance/${frameworkId}/controls/${controlId}`;
    const path = [base, subpath].flatMap((s) => s ?? []).join("/");
    const search = new URLSearchParams([
      ["spaceId", space.id],
      ["frameworkMrn", frameworkMrn],
      ["controlMrn", control.mrn],
      ...params,
    ]);
    return `${path}?${search}`;
  };

  const handleControlClick = (control: ControlNode) => {
    navigate(buildControlLink(control));
  };

  const tableHeaders: Header[] = [
    hasApplyExceptionPermission
      ? {
          id: "SELECT",
          label: (
            <Checkbox
              checked={isGroupChecked(targetPaginatedGroup)}
              indeterminate={isGroupIndeterminate(targetPaginatedGroup)}
              onChange={(event) =>
                onGroupCheckChange(event, targetPaginatedGroup)
              }
            />
          ),
          sortable: false,
        }
      : undefined,
    {
      id: "TITLE",
      label: "Control",
    },
    { id: "CHECKS", label: "Checks" },
    { id: "QUERIES", label: "Queries" },
    { id: "EXCEPTIONS", label: "Exceptions" },
    { id: "ASSETS", label: "Assets" },
    { id: "COMPLETION", label: "Completion" },
  ].flatMap((h) => h ?? []);

  const handleSetExceptionModalOpen = async () => {
    const exceptionDialogProps: SetExceptionDialogPayload = {
      target: "control",
      selectedEntities: selectedControls,
      onSave: handleSetException,
      shouldListEntities: true,
      targetData: {
        targetTitle: framework.name,
      },
    };

    dialogs.open(SetExceptionDialog, exceptionDialogProps);
  };

  const handleRemoveExceptionModalOpen = () => {
    dialogs.open(RemoveExceptionsGroupDialog, {
      onRemove: async (params) => {
        await handleRemoveExceptionGroups(params);
        setSelectedControls([]);
      },
      exceptionMrns: exceptionGroups.map((eg) => eg.mrn),
    });
  };

  return (
    <Grid container rowGap={3}>
      <Grid size={12}>
        <FilterBar
          searchId="compliance-controls"
          placeholder="compliance_controls"
          search={search}
          mb={0}
        />
      </Grid>
      <Grid component={Paper} size={12}>
        <ComplianceTable
          tableHeaders={tableHeaders}
          defaultActiveColumn={ControlsOrderField.Completion}
          selection={selectedControls}
          selectable={hasApplyExceptionPermission}
        >
          {controlsArray.slice(pageItems.from, pageItems.to).map((control) => {
            const controlId = control.mrn?.split("/").pop();
            const isSelected = Boolean(
              selectedControls.find((check) => check.mrn === control.mrn),
            );
            const exception = control.exception;
            const exceptionTooltipProps = {
              title: <ExceptionTooltipContent exception={exception ?? null} />,
            };
            const isRiskAccepted = control.state === ControlState.RiskAccepted;
            const isWorkaround = control.state === ControlState.Workaround;
            const isFalsePositive =
              control.state === ControlState.FalsePositive;
            const isDisabled = control.state === ControlState.Disabled;
            const isOutOfScope = control.state === ControlState.OutOfScope;
            const isInactive = control.state !== ControlState.Active;
            const isPending = exception?.status === ExceptionStatus.New;

            const hasException =
              isPending ||
              isRiskAccepted ||
              isDisabled ||
              isWorkaround ||
              isFalsePositive;

            const className = [
              isInactive ? "inactive" : "",
              isSelected ? "selected" : "",
            ].join(" ");

            return (
              <TableRow
                onClick={() => handleControlClick(control)}
                key={controlId}
                className={className}
              >
                {hasApplyExceptionPermission && (
                  <TableCell>
                    <Checkbox
                      onClick={(e) => handleNodeClick(e)}
                      onChange={(e, changed) =>
                        handleNodeChange(e, changed, {
                          mrn: control.mrn,
                          exception: exception,
                          title: control.title,
                        })
                      }
                      checked={Boolean(
                        selectedControls.find(
                          (check) => check.mrn === control.mrn,
                        ),
                      )}
                    />
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isOutOfScope && (
                      <OutOfScopeChip
                        author={exception?.author.name}
                        date={exception?.createdAt}
                      />
                    )}
                    {hasException && (
                      <ExceptionChip
                        isRiskAccepted={isRiskAccepted}
                        isDisabled={isDisabled}
                        isWorkaround={isWorkaround}
                        isFalsePositive={isFalsePositive}
                        onPendingExceptionClick={() => {
                          navigate(
                            `/space/compliance/${frameworkId}/exceptions?spaceId=${space.id}&frameworkMrn=${frameworkMrn}&exceptionId=${exception?.id}`,
                          );
                        }}
                        TooltipProps={exceptionTooltipProps}
                      />
                    )}
                    {control.title}
                  </Box>
                </TableCell>
                <TableCell>{control?.checksStats.totalChecks}</TableCell>
                <TableCell>{control?.checksStats.totalQueries}</TableCell>
                <TableCell>-</TableCell>
                <TableCell>{control?.assetsStats.totalAssets}</TableCell>
                <TableCell>
                  <Box sx={{ ...(isInactive && { opacity: 0.25 }) }}>
                    <CompletionCell
                      assets={control?.assetsStats.totalAssets}
                      completionData={{
                        percent: control.completion,
                        height: theme.spacing(1),
                        tooltip: `Completion: ${control.completion}%`,
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </ComplianceTable>
        <Pagination
          totalCount={complianceFramework?.controls?.totalCount ?? 0}
          setPageItems={setPageItems}
        />
      </Grid>
    </Grid>
  );
}
